import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '../../context/UserContext';
import TurtleExecutor from '../../services/turtleGraphics';
import codeCanvasTemplates from '../../data/codeCanvasTemplates';
import './VisualCodeCanvas.css';

function VisualCodeCanvas() {
  const { user } = useUser();
  const canvasRef = useRef(null);
  const executorRef = useRef(null);
  
  const [code, setCode] = useState(codeCanvasTemplates[0].code);
  const [selectedTemplate, setSelectedTemplate] = useState(codeCanvasTemplates[0]);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [savedArt, setSavedArt] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [artName, setArtName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Initialize canvas and executor
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      executorRef.current = new TurtleExecutor(canvas, ctx);
    }
  }, []);

  const runCode = useCallback(() => {
    if (!executorRef.current) return;

    setRunning(true);
    setError(null);

    try {
      const result = executorRef.current.executeCode(code);
      
      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    }

    setRunning(false);
  }, [code]);

  // Auto-run on code change (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (code.trim()) {
        runCode();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [code, runCode]);

  const clearCanvas = () => {
    if (executorRef.current) {
      executorRef.current.turtle.clear();
    }
    setError(null);
  };

  const loadTemplate = (template) => {
    setSelectedTemplate(template);
    setCode(template.code);
    setError(null);
  };

  const saveArt = () => {
    if (!artName.trim()) {
      alert('Please enter a name for your art!');
      return;
    }

    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL('image/png');
    
    const newArt = {
      id: Date.now(),
      name: artName,
      code: code,
      image: imageData,
      createdAt: new Date().toISOString(),
      author: user?.name || 'Anonymous'
    };

    setSavedArt([newArt, ...savedArt]);
    setShowSaveModal(false);
    setArtName('');
    alert('🎨 Art saved to your gallery!');
  };

  const downloadArt = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `code-art-${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const shareArt = () => {
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      if (navigator.share) {
        const file = new File([blob], 'my-code-art.png', { type: 'image/png' });
        navigator.share({
          title: 'Check out my code art!',
          text: 'I created this with code! 🎨',
          files: [file]
        }).catch(err => console.log('Share failed:', err));
      } else {
        alert('Sharing not supported on this device. Try downloading instead!');
      }
    });
  };

  return (
    <div className="visual-code-canvas">
      <div className="canvas-header">
        <h1>🎨 Visual Code Canvas</h1>
        <p>Write code, create art!</p>
      </div>

      <div className="canvas-controls">
        <div className="template-selector">
          <label>📚 Templates:</label>
          <select 
            value={selectedTemplate.id} 
            onChange={(e) => {
              const template = codeCanvasTemplates.find(t => t.id === e.target.value);
              loadTemplate(template);
            }}
          >
            {codeCanvasTemplates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name} ({template.difficulty})
              </option>
            ))}
          </select>
        </div>

        <div className="action-buttons">
          <button onClick={runCode} className="run-btn" disabled={running}>
            {running ? '⏳ Running...' : '▶️ Run Code'}
          </button>
          <button onClick={clearCanvas} className="clear-btn">
            🗑️ Clear
          </button>
          <button onClick={() => setShowSaveModal(true)} className="save-btn">
            💾 Save Art
          </button>
          <button onClick={downloadArt} className="download-btn">
            📥 Download
          </button>
          <button onClick={shareArt} className="share-btn">
            📤 Share
          </button>
          <button onClick={() => setShowGallery(!showGallery)} className="gallery-btn">
            🖼️ Gallery ({savedArt.length})
          </button>
        </div>
      </div>

      <div className="canvas-workspace">
        {/* Code Editor */}
        <div className="code-panel">
          <div className="panel-header">
            <h3>💻 Code Editor</h3>
            <span className="template-info">
              {selectedTemplate.name} - {selectedTemplate.description}
            </span>
          </div>
          
          <textarea
            className="code-editor"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Write your creative code here..."
            spellCheck={false}
          />

          {error && (
            <div className="error-panel">
              <strong>❌ Error:</strong>
              <p>{error}</p>
              <small>💡 Tip: Check your syntax and turtle commands!</small>
            </div>
          )}

          <div className="code-hints">
            <h4>🎯 Quick Commands:</h4>
            <ul>
              <li><code>turtle.forward(100)</code> - Move forward</li>
              <li><code>turtle.right(90)</code> - Turn right</li>
              <li><code>turtle.color('red')</code> - Change color</li>
              <li><code>turtle.circle(50)</code> - Draw circle</li>
              <li><code>turtle.width(5)</code> - Change width</li>
            </ul>
          </div>
        </div>

        {/* Canvas Preview */}
        <div className="canvas-panel">
          <div className="panel-header">
            <h3>🎨 Art Preview</h3>
            <span className="canvas-info">Live preview</span>
          </div>
          
          <div className="canvas-container">
            <canvas 
              ref={canvasRef}
              width={600}
              height={600}
              className="art-canvas"
            />
          </div>

          <div className="canvas-tips">
            <p>✨ Your art appears as you type!</p>
            <p>🎨 Experiment with colors, shapes, and loops</p>
            <p>🚀 Try changing numbers to see what happens</p>
          </div>
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>💾 Save Your Art</h2>
            <input
              type="text"
              placeholder="Give your art a name..."
              value={artName}
              onChange={(e) => setArtName(e.target.value)}
              className="art-name-input"
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={saveArt} className="confirm-btn">
                Save
              </button>
              <button onClick={() => setShowSaveModal(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery */}
      {showGallery && (
        <div className="gallery-overlay" onClick={() => setShowGallery(false)}>
          <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-header">
              <h2>🖼️ Your Art Gallery</h2>
              <button onClick={() => setShowGallery(false)} className="close-btn">
                ✕
              </button>
            </div>

            {savedArt.length === 0 ? (
              <div className="empty-gallery">
                <p>🎨 No saved art yet!</p>
                <p>Create something beautiful and save it!</p>
              </div>
            ) : (
              <div className="gallery-grid">
                {savedArt.map(art => (
                  <div key={art.id} className="gallery-item">
                    <img src={art.image} alt={art.name} />
                    <div className="gallery-item-info">
                      <h4>{art.name}</h4>
                      <p className="author">By {art.author}</p>
                      <p className="date">
                        {new Date(art.createdAt).toLocaleDateString()}
                      </p>
                      <button
                        onClick={() => {
                          setCode(art.code);
                          setShowGallery(false);
                        }}
                        className="load-code-btn"
                      >
                        📝 Load Code
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VisualCodeCanvas;
